// What a talent's STAGE and a project's PHASE mean on screen. The backend derives both
// (`recruiter/concierge/talent_dashboard.py`, one source of truth for the rail, the home and
// the overview); this module only owns their words and tones. Pure, unit-tested
// (talent-stage.test.ts). No long dashes: the copy gate walks this app.

import type { SellerProjectPhase, SellerTalentStage, SellerThreadSummary } from "@recruiter/shared";

/** A status tone, mapped to the Mira field palette in globals.css (`.sl-tone--*`). */
export type StageTone = "ai" | "wait" | "ok" | "slate" | "muted";

export interface StageMeta {
  /** The short status chip the rail shows. */
  label: string;
  tone: StageTone;
  /** The talent owes the next move: the rail pulses it and the home lists it first. */
  needsYou: boolean;
  /** The overview's status pill: a headline and one plain sentence under it. */
  headline: string;
  sub: string;
}

export const STAGE_META: Record<SellerTalentStage, StageMeta> = {
  new: {
    label: "New request",
    tone: "ai",
    needsYou: true,
    headline: "Mira has a project for you",
    sub: "Read the brief, then reply to Mira with your questions or your price.",
  },
  your_turn: {
    label: "Your reply",
    tone: "wait",
    needsYou: true,
    headline: "Mira is waiting for your reply",
    sub: "Answer in the conversation to keep your place in this project.",
  },
  with_client: {
    label: "With the client",
    tone: "slate",
    needsYou: false,
    headline: "Mira is checking with the client",
    sub: "Mira asked the client something for you. The answer will land in the conversation.",
  },
  proposal: {
    label: "Proposal sent",
    tone: "ai",
    needsYou: false,
    headline: "Your proposal is with the client",
    sub: "Mira shared it. You will hear here as soon as the client decides.",
  },
  revise: {
    label: "Changes asked",
    tone: "wait",
    needsYou: true,
    headline: "Mira asked for stronger terms",
    sub: "The client has not seen this proposal yet. Mira's note is in the conversation.",
  },
  picked: {
    label: "Picked",
    tone: "ok",
    needsYou: false,
    headline: "The client picked you",
    sub: "They chose your proposal. Expect to hear from them soon.",
  },
  direct: {
    label: "Client in touch",
    tone: "ok",
    needsYou: false,
    headline: "The client is talking with you directly",
    sub: "They took the conversation over, so their messages now come straight to you.",
  },
  declined: {
    label: "Not moving forward",
    tone: "muted",
    needsYou: false,
    headline: "This one is not moving forward",
    sub: "The conversation has ended. Mira will keep bringing you projects that fit.",
  },
  closed: {
    label: "Closed",
    tone: "muted",
    needsYou: false,
    headline: "This project has wrapped up",
    sub: "The client finished their search. Mira will keep bringing you projects that fit.",
  },
};

export interface PhaseMeta {
  label: string;
  /** Still moving: the chip carries the live dot. */
  live: boolean;
}

export const PHASE_META: Record<SellerProjectPhase, PhaseMeta> = {
  outreach: { label: "Collecting proposals", live: true },
  choosing: { label: "Client is choosing", live: true },
  closed: { label: "Wrapped up", live: false },
};

/** A project is "past" once it wrapped up or this talent's part in it ended. */
export function isPast(t: Pick<SellerThreadSummary, "phase" | "stage">): boolean {
  return t.phase === "closed" || t.stage === "declined" || t.stage === "closed";
}

/** The rail's two groups. Active: what needs the talent first, then the most recent.
 *  Past: most recent first. Input order is the backend's (by project name) and is
 *  deliberately not trusted for recency. */
export function groupThreads(threads: SellerThreadSummary[]): {
  active: SellerThreadSummary[];
  past: SellerThreadSummary[];
} {
  const byRecent = (a: SellerThreadSummary, b: SellerThreadSummary) =>
    (b.last_message_at || "").localeCompare(a.last_message_at || "");
  const active = threads.filter((t) => !isPast(t));
  const past = threads.filter(isPast).sort(byRecent);
  active.sort((a, b) => {
    const needA = STAGE_META[a.stage].needsYou ? 0 : 1;
    const needB = STAGE_META[b.stage].needsYou ? 0 : 1;
    return needA - needB || byRecent(a, b);
  });
  return { active, past };
}
